// ─────────────────────────────────────────────────────────────────────────────
// Reusable HTML snippets for HR Document Templates
// Allows quick insertion of professional tables, signature blocks, and callouts
// ─────────────────────────────────────────────────────────────────────────────

export interface TemplateSnippet {
  id: string;
  label: string;
  description: string;
  icon: string;
  html: string;
}

export const TEMPLATE_SNIPPETS: TemplateSnippet[] = [
  {
    id: 'salary_table',
    label: 'Salary & Compensation Table',
    description: 'Insert a formatted monthly & annual earnings breakdown table with standard tokens',
    icon: 'Table',
    html: `<table style="width: 100%; border-collapse: collapse; margin: 16px 0; font-family: sans-serif; font-size: 13px;">
  <thead>
    <tr style="background-color: #f1f5f9; border-bottom: 2px solid #cbd5e1; text-align: left;">
      <th style="padding: 10px 12px; font-weight: 600; color: #1e293b;">Component</th>
      <th style="padding: 10px 12px; font-weight: 600; color: #1e293b; text-align: right;">Monthly (INR)</th>
    </tr>
  </thead>
  <tbody>
    <tr style="border-bottom: 1px solid #e2e8f0;">
      <td style="padding: 8px 12px; color: #334155;">Basic Salary</td>
      <td style="padding: 8px 12px; color: #0f172a; text-align: right; font-family: monospace;">{{inr salary.basic}}</td>
    </tr>
    <tr style="border-bottom: 1px solid #e2e8f0;">
      <td style="padding: 8px 12px; color: #334155;">House Rent Allowance (HRA)</td>
      <td style="padding: 8px 12px; color: #0f172a; text-align: right; font-family: monospace;">{{inr salary.hra}}</td>
    </tr>
    <tr style="border-bottom: 1px solid #e2e8f0;">
      <td style="padding: 8px 12px; color: #334155;">Special Allowance</td>
      <td style="padding: 8px 12px; color: #0f172a; text-align: right; font-family: monospace;">{{inr salary.specialAllowance}}</td>
    </tr>
    <tr style="border-bottom: 1px solid #e2e8f0;">
      <td style="padding: 8px 12px; color: #334155;">Conveyance Allowance</td>
      <td style="padding: 8px 12px; color: #0f172a; text-align: right; font-family: monospace;">{{inr salary.conveyance}}</td>
    </tr>
    <tr style="border-bottom: 2px solid #cbd5e1;">
      <td style="padding: 8px 12px; color: #334155;">Medical Allowance</td>
      <td style="padding: 8px 12px; color: #0f172a; text-align: right; font-family: monospace;">{{inr salary.medicalAllowance}}</td>
    </tr>
    <tr style="background-color: #f8fafc; font-weight: 600;">
      <td style="padding: 10px 12px; color: #0f172a;">Gross Monthly Salary</td>
      <td style="padding: 10px 12px; color: #0f172a; text-align: right; font-family: monospace;">{{grossSalary}}</td>
    </tr>
    <tr style="background-color: #eff6ff; font-weight: 700;">
      <td style="padding: 10px 12px; color: #1e3a8a;">Annual Cost to Company (CTC)</td>
      <td style="padding: 10px 12px; color: #1e3a8a; text-align: right; font-family: monospace;">{{ctcAnnual}}</td>
    </tr>
  </tbody>
</table>`,
  },
  {
    id: 'signature_block',
    label: 'Formal Signature Block',
    description: 'Two-column employer and candidate acceptance signature section',
    icon: 'PenTool',
    html: `<div style="margin-top: 36px; padding-top: 24px; border-top: 1px solid #e2e8f0; display: flex; justify-content: space-between; font-family: sans-serif; font-size: 13px;">
  <div style="width: 45%;">
    <p style="margin: 0 0 4px 0; color: #64748b; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px;">For & on behalf of {{companyName}}</p>
    <div style="height: 48px;"></div>
    <p style="margin: 0; font-weight: 600; color: #0f172a;">{{signatoryName}}</p>
    <p style="margin: 2px 0 0 0; color: #475569; font-size: 12px;">{{signatoryTitle}}</p>
  </div>
  <div style="width: 45%;">
    <p style="margin: 0 0 4px 0; color: #64748b; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px;">Candidate Acceptance</p>
    <div style="height: 48px;"></div>
    <p style="margin: 0; font-weight: 600; color: #0f172a;">{{candidateName}}</p>
    <p style="margin: 2px 0 0 0; color: #475569; font-size: 12px;">Date: ____________________</p>
  </div>
</div>`,
  },
  {
    id: 'terms_callout',
    label: 'Key Employment Terms Callout',
    description: 'Highlighted callout box summarizing probation, notice period, and location',
    icon: 'AlertCircle',
    html: `<div style="background-color: #f8fafc; border: 1px solid #e2e8f0; border-left: 4px solid #3b82f6; border-radius: 6px; padding: 14px 16px; margin: 16px 0; font-family: sans-serif; font-size: 13px; color: #334155;">
  <div style="font-weight: 600; color: #0f172a; margin-bottom: 8px;">Key Employment Summary</div>
  <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px;">
    <div><strong>Role:</strong> {{designation}}</div>
    <div><strong>Department:</strong> {{department}}</div>
    <div><strong>Work Location:</strong> {{workLocation}}</div>
    <div><strong>Joining Date:</strong> {{joiningDate}}</div>
    <div><strong>Probation Period:</strong> {{probationPeriod}}</div>
    <div><strong>Notice Period:</strong> {{noticePeriod}}</div>
  </div>
</div>`,
  },
  {
    id: 'page_break',
    label: 'Page Break (New Page)',
    description: 'Insert a page break with automatic header and compact footer on the new page',
    icon: 'FileText',
    html: `<div style="page-break-before: always; margin-top: 24px;"></div>`,
  },
];
